// @ts-nocheck
import { useState } from "react";
import { Link } from "wouter";
import {
  useListCampaigns, useCreateCampaign, useDeleteCampaign, useSendCampaign, getListCampaignsQueryKey,
  useListDevices, useListTemplates, useListContactGroups,
} from "../lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../components/layout/PageHeader";
import { StatCard } from "../components/StatCard";
import { useLang } from "../lib/lang";

const API = import.meta.env.VITE_API_URL ?? "";

export function Campaigns() {
  const { t } = useLang();
  const d = t.campaigns;
  const qc = useQueryClient();
  const { data: campaigns, isLoading } = useListCampaigns({
    query: {
      refetchInterval: (query) => {
        const data = query.state.data as any[];
        if (data?.some((c: any) => c.status === "running" || c.status === "paused")) return 3000;
        return false;
      },
    },
  } as any);
  const { data: devices } = useListDevices();
  const { data: templates } = useListTemplates();
  const { data: groups } = useListContactGroups();
  const createCampaign = useCreateCampaign();
  const deleteCampaign = useDeleteCampaign();
  const sendCampaign = useSendCampaign();

  const [showForm, setShowForm] = useState(false);
  const [cName, setCName] = useState("");
  const [cDevice, setCDevice] = useState("");
  const [cTemplate, setCTemplate] = useState("");
  const [cGroup, setCGroup] = useState("");
  const [cMessage, setCMessage] = useState("");
  const [cDelayMin, setCDelayMin] = useState(20);
  const [cDelayMax, setCDelayMax] = useState(60);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const total = campaigns?.length ?? 0;
  const completed = campaigns?.filter(c => c.status === "completed").length ?? 0;
  const running = campaigns?.filter(c => c.status === "running").length ?? 0;

  const filtered = (campaigns ?? []).filter(c => {
    const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || c.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cDelayMin >= cDelayMax) {
      alert("Max delay must be greater than min delay.");
      return;
    }
    try {
      await createCampaign.mutateAsync({ data: { name: cName, deviceId: Number(cDevice), templateId: cTemplate ? Number(cTemplate) : undefined, groupId: cGroup ? Number(cGroup) : undefined, message: cMessage, delayMin: cDelayMin, delayMax: cDelayMax } } as any);
      setCName(""); setCDevice(""); setCTemplate(""); setCGroup(""); setCMessage(""); setCDelayMin(20); setCDelayMax(60);
      setShowForm(false);
      qc.invalidateQueries({ queryKey: getListCampaignsQueryKey() });
    } catch {
      alert("Failed to create campaign. Please try again.");
    }
  };

  const fmtDelay = (sec: number) => {
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60 > 0 ? `${sec % 60}s` : ""}`.trim();
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${h}h${m > 0 ? ` ${m}m` : ""}`;
  };

  const handleSend = async (id: number) => {
    try {
      await sendCampaign.mutateAsync({ id });
      qc.invalidateQueries({ queryKey: getListCampaignsQueryKey() });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("not connected") || msg.includes("422")) {
        alert("Device is not connected to WhatsApp.\nPlease go to My Devices and scan the QR code first.");
      } else {
        alert("Failed to send campaign: " + msg);
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this campaign?")) return;
    try {
      await deleteCampaign.mutateAsync({ id });
      qc.invalidateQueries({ queryKey: getListCampaignsQueryKey() });
    } catch {
      alert("Failed to delete campaign.");
    }
  };

  const handleDuplicate = async (id: number) => {
    try {
      await fetch(`${API}/api/campaigns/${id}/duplicate`, { method: "POST" });
      qc.invalidateQueries({ queryKey: getListCampaignsQueryKey() });
    } catch {
      alert("Failed to duplicate campaign.");
    }
  };

  const handlePause = async (id: number) => {
    try {
      await fetch(`${API}/api/campaigns/${id}/pause`, { method: "POST" });
      qc.invalidateQueries({ queryKey: getListCampaignsQueryKey() });
    } catch {
      alert("Failed to pause campaign.");
    }
  };

  const handleResume = async (id: number) => {
    try {
      await fetch(`${API}/api/campaigns/${id}/resume`, { method: "POST" });
      qc.invalidateQueries({ queryKey: getListCampaignsQueryKey() });
    } catch {
      alert("Failed to resume campaign.");
    }
  };

  const statusStyle = (status: string) => {
    const map: Record<string, string> = {
      completed: "bg-[#d4edda] text-[#155724]",
      running: "bg-[#cce5ff] text-[#004085]",
      scheduled: "bg-[#fff3cd] text-[#856404]",
      failed: "bg-[#f8d7da] text-[#721c24]",
      draft: "bg-[#e9ecef] text-[#525f7f]",
    };
    return map[status] ?? "bg-[#e9ecef] text-[#525f7f]";
  };

  return (
    <div>
      <PageHeader
        title={d.title}
        actions={[
          { label: d.newCampaign, icon: "fas fa-paper-plane", onClick: () => setShowForm(true) },
        ]}
      />

      <div className="px-6 pb-6 space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <StatCard label={d.totalCampaigns} value={campaigns?.reduce((a, c) => a + (c.sent ?? 0), 0).toLocaleString() ?? 0} iconClass="fas fa-rocket" gradient="primary" />
          <StatCard label={d.completed} value={completed} iconClass="fas fa-check-circle" gradient="success" />
          <StatCard label={d.running} value={running} iconClass="fas fa-spinner" gradient="info" />
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9ecef]">
                <h5 className="font-semibold text-[#32325d]">{d.title}</h5>
                <button onClick={() => setShowForm(false)} className="text-[#8898aa] hover:text-[#32325d]"><i className="fas fa-times" /></button>
              </div>
              <form onSubmit={handleCreate} className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.campaignName}</label>
                  <input value={cName} onChange={e => setCName(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" placeholder="Summer Sale Campaign" required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.device}</label>
                  <select value={cDevice} onChange={e => setCDevice(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" required>
                    <option value="">{d.selectDevice}</option>
                    {devices?.map(dev => <option key={dev.id} value={dev.id}>{dev.name} - {dev.phoneNumber}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.template} <span className="font-normal text-[#adb5bd]">({t.common.optional})</span></label>
                  <select
                    value={cTemplate}
                    onChange={e => {
                      setCTemplate(e.target.value);
                      const tpl = templates?.find(tp => String(tp.id) === e.target.value);
                      if (tpl?.content) setCMessage(tpl.content);
                      else if (!e.target.value) setCMessage("");
                    }}
                    className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]"
                  >
                    <option value="">{d.selectTemplate}</option>
                    {templates?.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">
                    {d.message}
                  </label>
                  <textarea value={cMessage} onChange={e => setCMessage(e.target.value)} rows={3} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef] resize-none" placeholder={d.orTypeMessage} required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.group} <span className="font-normal text-[#adb5bd]">({t.common.optional})</span></label>
                  <select value={cGroup} onChange={e => setCGroup(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]">
                    <option value="">{d.selectGroup}</option>
                    {groups?.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>

                {/* Delay settings */}
                <div className="border border-[#e9ecef] rounded-xl p-4 bg-[#f8f9fe] space-y-3">
                  <div className="flex items-center gap-2">
                    <i className="fas fa-clock text-[#1171ef] text-sm" />
                    <span className="text-xs font-semibold text-[#32325d]">{d.delayTitle}</span>
                  </div>

                  {/* Quick presets */}
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { label: "20s–1m", min: 20, max: 60 },
                      { label: "1–3m", min: 60, max: 180 },
                      { label: "3–10m", min: 180, max: 600 },
                      { label: "15–30m", min: 900, max: 1800 },
                      { label: "1–3h", min: 3600, max: 10800 },
                    ].map(p => (
                      <button
                        key={p.label}
                        type="button"
                        onClick={() => { setCDelayMin(p.min); setCDelayMax(p.max); }}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                          cDelayMin === p.min && cDelayMax === p.max
                            ? "gradient-primary text-white shadow-sm"
                            : "bg-white border border-[#e9ecef] text-[#525f7f] hover:border-[#1171ef] hover:text-[#1171ef]"
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-[#525f7f] mb-1">{d.minDelay}</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={20}
                          max={10799}
                          value={cDelayMin}
                          onChange={e => setCDelayMin(Math.max(20, Number(e.target.value)))}
                          className="w-full border border-[#cad1d7] rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]"
                        />
                        <span className="text-xs text-[#8898aa] shrink-0 w-10">{fmtDelay(cDelayMin)}</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-[#525f7f] mb-1">{d.maxDelay}</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={21}
                          max={10800}
                          value={cDelayMax}
                          onChange={e => setCDelayMax(Math.min(10800, Number(e.target.value)))}
                          className="w-full border border-[#cad1d7] rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]"
                        />
                        <span className="text-xs text-[#8898aa] shrink-0 w-10">{fmtDelay(cDelayMax)}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-[#8898aa]">
                    <i className="fas fa-info-circle mr-1" />
                    {d.delayHint.replace("{min}", fmtDelay(cDelayMin)).replace("{max}", fmtDelay(cDelayMax))}
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-[#e9ecef] rounded-lg text-[#525f7f]">{t.common.cancel}</button>
                  <button type="submit" disabled={createCampaign.isPending} className="px-4 py-2 text-sm gradient-primary text-white rounded-lg disabled:opacity-50">
                    {createCampaign.isPending ? t.common.creating : d.newCampaign}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef]">
          <div className="px-5 py-4 border-b border-[#e9ecef] flex flex-wrap items-center justify-between gap-3">
            <h4 className="text-sm font-semibold text-[#32325d]">{d.title}</h4>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <i className="fas fa-search absolute start-3 top-1/2 -translate-y-1/2 text-[#adb5bd] text-xs pointer-events-none" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={d.search}
                  className="border border-[#e9ecef] rounded-lg ps-8 pe-3 py-1.5 text-xs text-[#32325d] focus:outline-none focus:ring-1 focus:ring-[#1171ef] w-44"
                />
              </div>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className="border border-[#e9ecef] rounded-lg px-3 py-1.5 text-xs text-[#32325d] focus:outline-none focus:ring-1 focus:ring-[#1171ef]"
              >
                <option value="">{d.filterStatus}</option>
                {Object.entries(d.status).map(([k, v]) => (
                  <option key={k} value={k}>{v as string}</option>
                ))}
              </select>
            </div>
          </div>
          {isLoading ? (
            <div className="p-8 text-center text-[#8898aa]"><i className="fas fa-spinner fa-spin text-2xl" /></div>
          ) : (campaigns?.length ?? 0) === 0 ? (
            <div className="p-12 text-center">
              <i className="fas fa-rocket text-4xl text-[#cad1d7] mb-3 block" />
              <p className="text-[#8898aa] text-sm mb-1">{d.noCampaigns}</p>
              <p className="text-[#adb5bd] text-xs mb-4">{d.noCampaignsHint}</p>
              <button onClick={() => setShowForm(true)} className="gradient-primary text-white px-5 py-2 rounded-lg text-sm shadow-sm">
                <i className="fas fa-paper-plane me-2" />{d.newCampaign}
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="px-5 py-3 text-start text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">{d.campaignName}</th>
                    <th className="px-5 py-3 text-start text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">{d.progress}</th>
                    <th className="px-5 py-3 text-start text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">{t.common.status}</th>
                    <th className="px-5 py-3 text-start text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">{t.common.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={4} className="px-5 py-8 text-center text-sm text-[#8898aa]">{t.common.noData}</td></tr>
                  ) : filtered.map(c => {
                    const total = c.totalRecipients ?? 0;
                    const sent = c.sentCount ?? 0;
                    const delivered = c.deliveredCount ?? 0;
                    const failed = c.failedCount ?? 0;
                    const pending = Math.max(0, total - sent - failed);
                    const pct = total > 0 ? Math.round((sent / total) * 100) : 0;
                    const isLive = c.status === "running" || c.status === "paused";
                    return (
                      <tr key={c.id} className={`border-b border-[#f6f9fc] hover:bg-[#f8f9fe] ${c.status === "running" ? "bg-[#f0f5ff]" : c.status === "paused" ? "bg-[#fffdf0]" : ""}`}>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            {c.status === "running" && (
                              <span className="relative flex h-2 w-2 shrink-0">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1171ef] opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1171ef]" />
                              </span>
                            )}
                            {c.status === "paused" && <i className="fas fa-pause-circle text-[#fb6340] text-xs shrink-0" />}
                            <div>
                              <p className="text-sm font-semibold text-[#32325d]">{c.name}</p>
                              <p className="text-xs text-[#8898aa] mt-0.5">{total} {d.recipients}{isLive ? ` · ${pending} ${d.remaining}` : ""}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="flex-1 h-1.5 bg-[#e9ecef] rounded-full overflow-hidden">
                              <div className={`h-full rounded-full transition-all ${c.status === "running" ? "bg-[#1171ef]" : "gradient-primary"}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-[#8898aa] shrink-0">{pct}%</span>
                          </div>
                          <div className="flex gap-3 text-[11px] text-[#8898aa]">
                            <span><span className="text-[#2dce89] font-medium">{sent}</span> {t.common.sent}</span>
                            <span><span className="text-[#8898aa] font-medium">{pending}</span> {t.common.pending}</span>
                            <span><span className="text-[#f5365c] font-medium">{failed}</span> {t.common.failed}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${statusStyle(c.status)}`}>
                            {c.status === "running" && <i className="fas fa-circle-notch fa-spin mr-1 text-[10px]" />}
                            {d.status[c.status as keyof typeof d.status] ?? c.status}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Link href={`/campaigns/${c.id}`} className="flex items-center gap-1 text-xs text-[#1171ef] hover:underline">
                              <i className="fas fa-eye" />{d.detail}
                            </Link>
                            {c.status === "draft" && (
                              <button onClick={() => handleSend(c.id)} className="flex items-center gap-1 text-xs text-[#2dce89] hover:underline">
                                <i className="fas fa-paper-plane" />{d.start}
                              </button>
                            )}
                            {(c.status === "completed" || c.status === "failed") && (
                              <button onClick={() => handleSend(c.id)} className="flex items-center gap-1 text-xs text-[#1171ef] hover:underline" title="Resend this campaign to all contacts">
                                <i className="fas fa-redo" />{d.resend}
                              </button>
                            )}
                            {c.status === "running" && (
                              <button
                                onClick={() => handlePause(c.id)}
                                className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg bg-[#fb6340] text-white hover:bg-[#e85a38] transition-colors ml-1"
                                title="Pause campaign"
                              >
                                <i className="fas fa-pause text-[10px]" />{d.pause}
                              </button>
                            )}
                            {c.status === "paused" && (
                              <button
                                onClick={() => handleResume(c.id)}
                                className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg bg-[#2dce89] text-white hover:bg-[#24a974] transition-colors ml-1"
                                title="Resume campaign"
                              >
                                <i className="fas fa-play text-[10px]" />{d.resume}
                              </button>
                            )}
                            <button onClick={() => handleDuplicate(c.id)} className="flex items-center gap-1 text-xs text-[#8898aa] hover:text-[#1171ef] hover:underline ml-1">
                              <i className="fas fa-copy" />{d.duplicate}
                            </button>
                            <button onClick={() => handleDelete(c.id)} className="text-xs text-[#f5365c] hover:underline ml-1">
                              <i className="fas fa-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
